/**
 * Payment Schedule v1.0 §5.2 — customer-facing block on the hosted accept
 * page that surfaces every stage of the agreed payment plan and (when
 * required) captures the auto-charge consent in a single signing moment.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { fetchPublicPaymentPlanPreview } from '@/lib/quote/publicPaymentPlanCache';
import { paymentPlanRows } from '@/lib/quote/paymentPlanRows';

const OPS_URL = import.meta.env.VITE_OPERATIONS_URL ?? '';

export interface PublicPaymentPlanStage {
  position: number;
  label: string;
  triggerDescription: string;
  amount: { kind: string; amount: number | null };
  autoCharge: boolean;
  action: string;
}

export interface PublicPaymentPlanPreview {
  templateId: string;
  previewText: string | null;
  consentRequired: boolean;
  stages: PublicPaymentPlanStage[];
  grandTotal: number;
  /** Raw column: 25 for a PERCENT deposit, not $250. Prefer `depositAmount`. */
  depositValue: number;
  /** The deposit in money, already netted out of any remaining stage. */
  depositAmount?: number;
  /**
   * The exact limits the approval will record, from the same server helper.
   * Absent only on payloads built before it was returned.
   */
  consentLimits?: { maxAmountPerCharge: number; maxTotal: number };
}

/**
 * What is left of the job once the deposit and every concrete stage are taken
 * off, for a plan that did not resolve its own remaining stage.
 *
 * The server resolves every stage to money, so its payloads never need this.
 * A plan built client-side still carries `null` for a remaining stage, and
 * printing that as $0.00 would understate it to the customer.
 */
function resolvePaymentPlanRemainder(preview: PublicPaymentPlanPreview): number {
  const accountedFor = preview.stages.reduce(
    (sum, stage) => sum + (stage.amount.amount ?? 0),
    0,
  );
  return Math.max(preview.grandTotal - (preview.depositAmount ?? 0) - accountedFor, 0);
}

/**
 * What the consent sentence tells the customer they are authorising.
 *
 * The server resolves every stage to money — including `remaining_balance`,
 * which it sizes net of the deposit — so this reads those figures rather than
 * re-deriving them. It used to compute its own `grandTotal - stages` remainder,
 * which ignored the deposit and so overstated the authorisation: on a $1,000
 * job with a 25% deposit it asked the customer to authorise $1,000 for a stage
 * that can only ever bill $750.
 */
export function resolvePaymentPlanConsentLimits(preview: PublicPaymentPlanPreview): {
  maxAmountPerCharge: number;
  maxTotal: number;
} {
  // Prefer the server's figures: they are what the ConsentRecord will actually
  // say. Re-deriving them from the rendered stage amounts made the disclosure
  // disagree with the record — the caps are order-independent and gross-derived,
  // while the amounts on screen are per-stage charges. The page read "max per
  // charge $750" where approval stored $1,000, and disclosed a $0 cap for a plan
  // whose auto-charge stage nets to $0 in authored order but charges $250 once
  // another invoice takes the deposit. A customer cannot consent to a number
  // they were never shown.
  if (preview.consentLimits) return preview.consentLimits;

  // Fallback only for a client-built plan that carries no limits.
  const remaining = resolvePaymentPlanRemainder(preview);
  const authorizedAmounts = preview.stages
    .filter((stage) => stage.autoCharge)
    .map((stage) => stage.amount.amount ?? remaining);

  return {
    maxAmountPerCharge: Math.max(0, ...authorizedAmounts),
    maxTotal: Math.round(authorizedAmounts.reduce((sum, amount) => sum + amount, 0) * 100) / 100,
  };
}

interface Props {
  estimateId: string;
  tenantId: string;
  companyName: string;
  consentChecked: boolean;
  onConsentChange: (next: boolean) => void;
  /** Notifies the parent whether the loaded preview requires consent. */
  onConsentRequiredChange?: (next: boolean) => void;
  /** Ask the server to resolve stages against the frozen member-priced offer. */
  acceptMembershipOffer?: boolean;
  /**
   * The membership billing option the customer actually picked. The approval
   * sends it, so the consent shown here has to be priced from it too — without
   * it this modal reads the snapshot default (say monthly) while the estimate
   * is approved on another (annual), and the caps the customer agreed to
   * describe a plan nobody bought.
   */
  membershipBillingOptionId?: string | null;
}

export function PaymentPlanPreview({
  estimateId,
  tenantId,
  companyName,
  consentChecked,
  onConsentChange,
  onConsentRequiredChange,
  acceptMembershipOffer = false,
  membershipBillingOptionId = null,
}: Props) {
  const [preview, setPreview] = useState<PublicPaymentPlanPreview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // The one request builder, shared with the approval and PDF paths, so the
    // consent preview cannot be priced from a different membership option than
    // the one being approved.
    fetchPublicPaymentPlanPreview<PublicPaymentPlanPreview>({
      operationsUrl: OPS_URL,
      id: estimateId,
      tenantId,
      acceptMembershipOffer,
      membershipBillingOptionId,
    })
      .then((data) => {
        if (!cancelled) {
          setPreview(data ?? null);
          onConsentRequiredChange?.(Boolean(data?.consentRequired));
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPreview(null);
          onConsentRequiredChange?.(false);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    acceptMembershipOffer,
    membershipBillingOptionId,
    estimateId,
    tenantId,
    onConsentRequiredChange,
  ]);

  if (loading) {
    return (
      <div className="border rounded-lg p-4 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading payment plan…
      </div>
    );
  }
  if (!preview || preview.stages.length === 0) return null;

  const remaining = resolvePaymentPlanRemainder(preview);
  const consentLimits = resolvePaymentPlanConsentLimits(preview);
  // NOT-235: match the document body's currency formatting (thousands
  // separators, normal font) instead of the hand-rolled monospace toFixed.
  const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

  return (
    <div className="border rounded-lg bg-white p-4 space-y-3">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        Payment Plan
      </h3>
      <>
          {preview.previewText && <p className="text-sm">{preview.previewText}</p>}
          <ul className="space-y-2 text-sm">
            {/* Rows, not stages — `paymentPlanRows` puts the deposit first, so
                every renderer of this payload shows the same plan. */}
            {paymentPlanRows(preview).map((s) => (
              <li
                key={s.key}
                className="flex items-baseline justify-between gap-3 border-b last:border-0 pb-2 last:pb-0"
              >
                <div>
                  <div className="font-medium">{s.label}</div>
                  <div className="text-xs text-muted-foreground">
                    {s.detail}
                    {s.autoCharge && (
                      <span className="ml-2 inline-block bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                        Auto-charged to your saved card
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  {/* Server payloads resolve every stage to money, deposit
                      netted out. A client-built plan can still carry `null`;
                      printing that as $0.00 would understate the stage. */}
                  {currency.format(s.amount ?? remaining)}
                  {s.isRemaining ? ' (remaining)' : ''}
                </div>
              </li>
            ))}
          </ul>
          <div className="flex justify-between text-sm font-semibold border-t pt-2">
            <span>Total</span>
            <span>{currency.format(preview.grandTotal)}</span>
          </div>
      </>

      {preview.consentRequired && (
        <label className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 rounded p-3 mt-2">
          <input
            type="checkbox"
            checked={consentChecked}
            onChange={(e) => onConsentChange(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            I authorize {companyName} to charge my saved payment method for the payment schedule
            {' shown above'}.
            Maximum per charge: {currency.format(consentLimits.maxAmountPerCharge)}; total cap:{' '}
            {currency.format(consentLimits.maxTotal)}.
          </span>
        </label>
      )}
    </div>
  );
}
